import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FolderKanban, Ruler, Package, Stethoscope, Globe, Calendar, Settings as SettingsIcon, ShoppingCart, Users, Tag, Briefcase, UserCog, BadgePlus, ClipboardList, FileText, ShieldCheck, Activity, Sparkles, Boxes, Receipt, LogOut, BookOpen, FileSpreadsheet, Wallet, Upload, NotebookPen, TrendingUp, Scale, Truck, KeyRound, MessageSquare, BarChart3, Megaphone, PenLine, Repeat, Bell, Camera, LineChart, Bot, Search as SearchIcon } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import useTallyShortcuts, { SHORTCUT_HINTS } from "../../hooks/useTallyShortcuts";
import InstallPWA from "../../components/InstallPWA";

const NAV = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/admin/categories", icon: FolderKanban, label: "Categories" },
    { to: "/admin/sizes", icon: Ruler, label: "Sizes" },
    { to: "/admin/products", icon: Package, label: "Products" },
    { to: "/admin/bulk-products", icon: Sparkles, label: "Bulk add (AI)" },
    { to: "/admin/photo-coverage", icon: Camera, label: "Photo coverage" },
    { to: "/admin/stock", icon: Boxes, label: "Stock" },
    { to: "/admin/distributor-pricing", icon: Tag, label: "Distributor Pricing" },
    { to: "/admin/doctors-registered", icon: Stethoscope, label: "Registered Doctors" },
    { to: "/admin/appointments", icon: Calendar, label: "Appointments" },
    { to: "/admin/doctors-internet", icon: Globe, label: "Internet Doctors" },
    { to: "/admin/distributors", icon: Briefcase, label: "Distributors" },
    { to: "/admin/distributor-hub", icon: TrendingUp, label: "Distributor Hub" },
    { to: "/admin/employees", icon: UserCog, label: "Employees" },
    { to: "/admin/designations", icon: BadgePlus, label: "Roles & access" },
    { to: "/admin/drs-fields", icon: ClipboardList, label: "DRS Fields" },
    { to: "/admin/invoice-templates", icon: FileText, label: "Invoice Templates" },
    { to: "/admin/orders", icon: ShoppingCart, label: "Orders" },
    { to: "/admin/order-status", icon: Truck, label: "Order status & dispatch" },
    { to: "/admin/packing-slips", icon: FileText, label: "Packing slips" },
    { to: "/admin/crm", icon: UserCog, label: "CRM (sales pipeline)" },
    { to: "/admin/mr-expenses", icon: Receipt, label: "MR Expenses" },
    { to: "/admin/drs-analytics", icon: BarChart3, label: "DRS analytics" },
    { to: "/admin/reviews", icon: MessageSquare, label: "Reviews" },
    { to: "/admin/blog", icon: PenLine, label: "Blog & CMS" },
    { to: "/admin/marketing", icon: Megaphone, label: "Marketing" },
    { to: "/admin/notifications", icon: Bell, label: "Notifications" },
    { to: "/admin/subscriptions", icon: Repeat, label: "Auto-refills" },
    { to: "/admin/subscriptions/analytics", icon: TrendingUp, label: "Sub. analytics" },
    // ── Billing ──
    { to: "/admin/invoices", icon: FileText, label: "Invoices", section: "billing" },
    { to: "/admin/external-invoices", icon: Upload, label: "External invoices", section: "billing" },
    { to: "/admin/customers", icon: Users, label: "Customer master", section: "billing" },
    { to: "/admin/dues", icon: Wallet, label: "All dues", section: "billing" },
    // ── Accounts Hub (Tally-style) ──
    { to: "/admin/daybook", icon: BookOpen, label: "Day Book", section: "accounts" },
    { to: "/admin/sales-register", icon: FileSpreadsheet, label: "Sales Register", section: "accounts" },
    { to: "/admin/outstanding", icon: Wallet, label: "Outstanding", section: "accounts" },
    { to: "/admin/stock-summary", icon: Boxes, label: "Stock Summary", section: "accounts" },
    { to: "/admin/journal", icon: NotebookPen, label: "Journal", section: "accounts" },
    { to: "/admin/pnl", icon: TrendingUp, label: "Profit & Loss", section: "accounts" },
    { to: "/admin/balance-sheet", icon: Scale, label: "Balance Sheet", section: "accounts" },
    { to: "/admin/gst-returns", icon: Receipt, label: "GST Returns", section: "accounts" },
    // ── Analytics Hub (Traffic / SEO / GEO / Sales) ──
    { to: "/admin/traffic", icon: LineChart, label: "Traffic", section: "analytics" },
    { to: "/admin/seo", icon: SearchIcon, label: "SEO", section: "analytics" },
    { to: "/admin/geo", icon: Bot, label: "GEO · AI", section: "analytics" },
    { to: "/admin/sales-analytics", icon: BarChart3, label: "Sales analytics", section: "analytics" },
    { to: "/admin/users", icon: Users, label: "Users" },
    { to: "/admin/editors", icon: ShieldCheck, label: "Editors", adminOnly: true },
    { to: "/admin/activity", icon: Activity, label: "Activity log" },
    { to: "/admin/legal", icon: Scale, label: "Legal pages" },
    { to: "/admin/feature-flags", icon: Sparkles, label: "Features & toggles" },
    { to: "/admin/settings", icon: SettingsIcon, label: "Settings" },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const isAdmin = user?.role === "admin";
    const items = NAV.filter((n) => !n.adminOnly || isAdmin);
    const handleLogout = () => { logout(); nav("/login"); };
    useTallyShortcuts();

    return (
        <div className="min-h-screen bg-sky-50/40" data-testid="admin-layout">
            {/* Top bar — always-visible Sign out button */}
            <header className="sticky top-0 z-30 bg-slate-900 text-stone-100 border-b border-slate-800">
                <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="w-7 h-7 grid place-items-center bg-sky-700 text-stone-50 font-serif text-sm">H</span>
                        <span className="font-serif text-base">Hahnemann <span className="text-[10px] tracking-[0.3em] uppercase text-stone-400 ml-1">{isAdmin ? "Admin" : "Editor"}</span></span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-stone-400 hidden sm:inline" title={user?.email}>{user?.email}</span>
                        <button
                            onClick={handleLogout}
                            className="text-xs text-stone-200 hover:text-white border border-slate-700 hover:border-sky-600 hover:bg-slate-800 px-3 py-1.5 inline-flex items-center gap-1 transition-colors"
                            data-testid="admin-logout-top"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1500px] mx-auto grid grid-cols-12">
                <aside className="col-span-3 lg:col-span-2 bg-slate-900 text-stone-100 sticky top-12 self-start h-[calc(100vh-3rem)] flex flex-col">
                    <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                        {items.map((n, i) => {
                            const prev = items[i - 1];
                            const sectionStart = n.section && prev?.section !== n.section;
                            const sectionLabel = n.section === "billing"
                                ? "Billing & Accounts"
                                : n.section === "accounts"
                                    ? "Accounts Hub · Tally-style"
                                    : n.section === "analytics"
                                        ? "Analytics Hub"
                                        : null;
                            return (
                                <React.Fragment key={n.to}>
                                    {sectionStart && sectionLabel && (
                                        <div className="pt-4 pb-1 px-3 text-[9px] tracking-[0.25em] uppercase text-stone-500 border-t border-stone-800 mt-3">
                                            {sectionLabel}
                                        </div>
                                    )}
                                    <NavLink
                                        to={n.to}
                                        end={n.end}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2 text-sm rounded-none transition-colors ${
                                                isActive ? "bg-sky-700 text-stone-50" : "text-stone-300 hover:bg-stone-800"
                                            }`
                                        }
                                        data-testid={`admin-nav-${n.label.toLowerCase().replace(/&/g, "and").replace(/ /g, "-")}`}
                                    >
                                        <n.icon className="w-4 h-4" /> {n.label}
                                    </NavLink>
                                </React.Fragment>
                            );
                        })}
                    </nav>
                    {/* Pinned to bottom of viewport — no matter how many nav items */}
                    <div className="border-t border-stone-800 p-4 bg-slate-900">
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Signed in as</div>
                        <div className="text-xs text-stone-300 truncate mb-3" title={user?.email}>{user?.email}</div>
                        <NavLink
                            to="/account/change-password"
                            className="w-full text-xs text-stone-200 hover:text-white flex items-center justify-center gap-2 px-3 py-2 border border-stone-700 hover:border-sky-600 hover:bg-slate-800 transition-colors mb-2"
                            data-testid="admin-change-password"
                        >
                            <KeyRound className="w-3.5 h-3.5" /> Change password
                        </NavLink>
                        <button
                            onClick={handleLogout}
                            className="w-full text-xs text-stone-200 hover:text-white flex items-center justify-center gap-2 px-3 py-2 border border-stone-700 hover:border-sky-600 hover:bg-slate-800 transition-colors"
                            data-testid="admin-logout"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </aside>
                <main className="col-span-9 lg:col-span-10 p-6 lg:p-10">
                    <Outlet />
                    <div className="mt-10 pt-4 border-t border-stone-200 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-stone-500" data-testid="shortcut-hints">
                        <span className="uppercase tracking-widest text-stone-400">Shortcuts</span>
                        {SHORTCUT_HINTS.map((s) => (
                            <span key={s.key} className="font-mono">
                                <kbd className="px-1 py-0.5 bg-stone-100 border border-stone-200 text-stone-700">{s.key}</kbd>
                                <span className="ml-1">{s.label}</span>
                            </span>
                        ))}
                    </div>
                </main>
            </div>
            <InstallPWA />
        </div>
    );
}
